// src/features/dashboard/components/RecentItemsTable.tsx

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Skeleton from '../../../components/Skeleton';
import { getRandomBorderColor } from '../../../utils/randomColor';

interface Column<T> {
  header: string;
  render: (item: T) => React.ReactNode;
}

interface RecentItemsTableProps<T> {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  items: T[];
  columns: Column<T>[];
  emptyMessage: string;
  onViewAll: () => void;
  onRowClick: (item: T) => void;
  viewAllColor: string;
  delay: number;
  loading?: boolean;
}

// Generic table component for displaying recent items
export function RecentItemsTable<T extends { id: number | string }>({
  title,
  icon: Icon,
  iconColor,
  items,
  columns,
  emptyMessage,
  onViewAll,
  onRowClick,
  viewAllColor,
  delay,
  loading = false,
}: RecentItemsTableProps<T>) {
  // Generate a single color for the whole table (header + all rows)
  const tableColor = useMemo(() => getRandomBorderColor(), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="theme-card rounded-lg border theme-border"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b theme-border">
        <h2 className="text-base font-semibold theme-card-title flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          {title}
        </h2>
        {loading ? (
          <Skeleton variant="text" width={60} height={16} />
        ) : (
          <button
            onClick={onViewAll}
            className={`text-xs ${viewAllColor} hover:opacity-80 flex items-center gap-1 font-medium`}
          >
            View All <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="overflow-x-auto">
          <table className="monday-table">
            <thead style={{ borderLeft: `7px solid ${tableColor}` }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((_, colIndex) => (
                      <td key={colIndex}>
                        <Skeleton variant="text" width="80%" height={16} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => onRowClick(item)}
                    style={{ borderLeft: `7px solid ${tableColor}` }}
                  >
                    {columns.map((col, idx) => (
                      <td key={idx}>{col.render(item)}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 theme-card-subtitle">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
